import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Button, Badge, Input, Modal, Tabs } from '../../../components/ui';
import { Trophy, Calendar, RefreshCw, Trash2, Edit, Gift, Settings, Save, ChevronDown, ChevronRight, Store, AlertTriangle, DollarSign, TrendingUp, TrendingDown } from 'lucide-react';
import { useAuth } from '../../../app/auth';
import { fetchAPI } from '../../../services/apiClient';
import { fechaLocalYMD, diaOperativo } from '../../../utils/date';
import { formatTicketForPrint } from '../../../utils/ticketFormat';

// Tipos
interface Sorteo {
  id: string;
  tipo_id: string;
  nombre: string;
  fecha: string;
  hora_apertura: string;
  hora_cierre: string;
  estado: string;
  auto_activar: boolean;
  auto_cerrar: boolean;
}

interface NumeroGanador {
  id: number;
  sorteo_id: string;
  sorteo_nombre: string;
  tipo_id: string;
  fecha: string;
  hora_cierre: string;
  num1: string;
  num2: string | null;
  num3: string | null;
  tipo_premio: string | null;
  tipo_monazos: string | null;
  paga_reventado: boolean | null;
  registrado_por: string;
  created_at: string;
  verificado: boolean;
}

interface SorteoPendiente {
  id: string;
  nombre: string;
  tipo_id: string;
  fecha: string;
  hora_cierre: string;
  estado: string;
}

interface SorteoConPremio {
  sorteo: Sorteo;
  ganador: NumeroGanador | null;
}

interface ReglaPremio {
  id: number;
  banca_id: string;
  local_id: string | null;
  sorteo_nombre: string;
  concepto: string;
  multiplicador: number;
  activo: boolean;
  created_at: string;
  updated_at: string | null;
}


interface LocalSimple {
  id: string;
  nombre: string;
}

interface SorteoSimple {
  id: string;
  nombre: string;
  tipo_id: string;
}

// Configuracion de tipos de sorteo
const TIPOS_CONFIG: Record<string, { label: string; color: string }> = {
  NORMAL: { label: 'Normal', color: 'bg-blue-600' },
  REVENTADO: { label: 'Reventado', color: 'bg-orange-600' },
  MEGA: { label: 'Mega', color: 'bg-red-600' },
  MONAZOS: { label: 'Monazos', color: 'bg-purple-600' },
  PARLEY: { label: 'Parley', color: 'bg-pink-600' },
  FECHAS: { label: 'Fechas', color: 'bg-green-600' },
};

const MESES_CORTO = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];
const MESES_FULL = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

// Formatear premio para mostrar
function formatearPremio(g: NumeroGanador): string {
  switch (g.tipo_id) {
    case 'NORMAL':
      return `#${g.num1.padStart(2, '0')}`;
    case 'REVENTADO':
      return `#${g.num1.padStart(2, '0')} (Reventado: ${g.paga_reventado ? 'SI' : 'NO'})`;
    case 'MEGA': {
      let megaStr = `#${g.num1.padStart(2, '0')}`;
      if (g.paga_reventado && g.num2) megaStr += ` + #${g.num2.padStart(2, '0')}`;
      megaStr += ` (Mega Reventado: ${g.paga_reventado ? 'SI' : 'NO'})`;
      return megaStr;
    }
    case 'PARLEY':
      return `#${g.num1.padStart(2, '0')} - #${(g.num2 || '00').padStart(2, '0')} - #${(g.num3 || '00').padStart(2, '0')}`;
    case 'FECHAS': {
      const [dia, mes] = g.num1.split('-');
      const mesIdx = parseInt(mes, 10) - 1;
      return `${dia} ${MESES_CORTO[mesIdx] || mes}`;
    }
    case 'MONAZOS':
      return `#${g.num1}${g.tipo_monazos ? ` (${g.tipo_monazos})` : ''}`;
    default:
      return g.num1;
  }
}

// Labels legibles para conceptos
const CONCEPTO_LABELS: Record<string, string> = {
  DIRECTO: 'Directo',
  REVENTADO: 'Reventado',
  MEGA_REVENTADO: 'Mega Reventado',
  EN_ORDEN: 'En Orden',
  DESORDEN: 'Desorden',
  PRIMEROS_2: 'Primeros 2',
  ULTIMOS_2: 'Ultimos 2',
};

// Labels para todos los tipos de sorteo (incluye MONAZOS)
const TIPO_LABELS: Record<string, string> = {
  NORMAL: 'Normal',
  REVENTADO: 'Reventado',
  MEGA: 'Mega',
  PARLEY: 'Parley',
  MONAZOS: 'Monazos',
  FECHAS: 'Fechas',
};


// ========================
// TAB 1: PREMIOS (existente)
// ========================
const PremiosTab: React.FC = () => {
  const { user } = useAuth();

  // Data
  const [sorteos, setSorteos] = useState<Sorteo[]>([]);
  const [ganadores, setGanadores] = useState<NumeroGanador[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pendientes, setPendientes] = useState<SorteoPendiente[]>([]);

  // Filtros
  const [fechaFiltro, setFechaFiltro] = useState(fechaLocalYMD());
  const [tipoFiltro, setTipoFiltro] = useState('');

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'asignar' | 'editar'>('asignar');
  const [sorteoSeleccionado, setSorteoSeleccionado] = useState<Sorteo | null>(null);
  const [ganadorEditando, setGanadorEditando] = useState<NumeroGanador | null>(null);
  const [guardando, setGuardando] = useState(false);

  // Formulario
  const [formData, setFormData] = useState({
    num1: '',
    num2: '',
    num3: '',
    paga_reventado: false,
    tipo_monazos: '',
    dia: '',
    mes: '',
  });

  // Modal corrección
  const [correccionOpen, setCorreccionOpen] = useState(false);
  const [ganadorCorrigiendo, setGanadorCorrigiendo] = useState<NumeroGanador | null>(null);
  const [sorteoCorrigiendo, setSorteoCorrigiendo] = useState<Sorteo | null>(null);
  const [correccionForm, setCorreccionForm] = useState({
    num1: '', num2: '', num3: '', paga_reventado: false, tipo_monazos: '', dia: '', mes: '',
  });
  const [correccionMotivo, setCorreccionMotivo] = useState('');
  const [correccionGuardando, setCorreccionGuardando] = useState(false);
  const [correccionError, setCorreccionError] = useState('');

  // Cargar datos
  const cargarDatos = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [sorteosData, ganadoresData, pendientesData] = await Promise.all([
        fetchAPI<Sorteo[]>(`/sorteos?fecha=${fechaFiltro}`),
        fetchAPI<NumeroGanador[]>(`/ganadores?fecha=${fechaFiltro}${tipoFiltro ? `&tipo_id=${tipoFiltro}` : ''}`),
        fetchAPI<SorteoPendiente[]>('/sorteos/pendientes-ganador'),
      ]);

      // REGLA CORRECTA: Un sorteo aparece en Premios para asignar ganador si:
      // 1. Fecha pasada: todos (ciclo completado)
      // 2. Fecha actual: estado = CERRADO O hora_cierre ya pasó O ya tiene ganador asignado
      const ahora = new Date();
      const horaActual = `${String(ahora.getHours()).padStart(2, '0')}:${String(ahora.getMinutes()).padStart(2, '0')}`;
      const esFechaPasada = fechaFiltro < fechaLocalYMD();
      const ganadoresIds = new Set(ganadoresData.map(g => g.sorteo_id));
      
      const filteredSorteos = sorteosData.filter(s => {
        // Tipo válido
        if (!(s.tipo_id in TIPOS_CONFIG)) return false;
        
        // Filtro por tipo si está activo
        if (tipoFiltro && s.tipo_id !== tipoFiltro) return false;
        
        // Para fechas pasadas: mostrar todos (ciclo completado)
        if (esFechaPasada) return true;

        // Siempre mostrar si ya tiene ganador asignado (aunque el sorteo esté abierto)
        if (ganadoresIds.has(s.id)) return true;
        
        // Para fecha actual: si CERRADO (inmediato) O si hora_cierre ya pasó
        return s.estado === 'CERRADO' || horaActual >= s.hora_cierre;
      });

      setSorteos(filteredSorteos);
      setGanadores(ganadoresData);
      // Filtrar pendientes: excluir los que ya se ven en la lista de la fecha seleccionada
      const sorteosIds = new Set(filteredSorteos.map(s => s.id));
      setPendientes(pendientesData.filter(p => !(sorteosIds.has(p.id) && p.fecha === fechaFiltro)));
    } catch (e: any) {
      setError(e.message || 'Error de conexion');
    } finally {
      setLoading(false);
    }
  }, [fechaFiltro, tipoFiltro]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  // Merge sorteos con ganadores
  const sorteosConPremio: SorteoConPremio[] = sorteos.map(s => ({
    sorteo: s,
    ganador: ganadores.find(g => g.sorteo_id === s.id) || null,
  }));

  // Stats
  const totalSorteos = sorteosConPremio.length;
  const conPremio = sorteosConPremio.filter(sp => sp.ganador !== null).length;
  const sinPremio = totalSorteos - conPremio;
  const confirmados = sorteosConPremio.filter(sp => sp.ganador?.verificado).length;

  // Abrir modal para asignar
  const abrirModalAsignar = (sorteo: Sorteo) => {
    setSorteoSeleccionado(sorteo);
    setGanadorEditando(null);
    setModalMode('asignar');
    setFormData({ num1: '', num2: '', num3: '', paga_reventado: false, tipo_monazos: '', dia: '', mes: '' });
    setModalOpen(true);
  };

  // Abrir modal para editar
  const abrirModalEditar = (sorteo: Sorteo, ganador: NumeroGanador) => {
    setSorteoSeleccionado(sorteo);
    setGanadorEditando(ganador);
    setModalMode('editar');
    if (sorteo.tipo_id === 'FECHAS') {
      const [dia, mes] = ganador.num1.split('-');
      setFormData({
        num1: '', num2: '', num3: '',
        paga_reventado: ganador.paga_reventado || false,
        tipo_monazos: '',
        dia: dia,
        mes: MESES_FULL[parseInt(mes, 10) - 1] || '',
      });
    } else {
      setFormData({
        num1: ganador.num1,
        num2: ganador.num2 || '',
        num3: ganador.num3 || '',
        paga_reventado: ganador.paga_reventado || false,
        tipo_monazos: ganador.tipo_monazos || '',
        dia: '', mes: '',
      });
    }
    setModalOpen(true);
  };

  const cerrarModal = () => {
    setModalOpen(false);
    setSorteoSeleccionado(null);
    setGanadorEditando(null);
    setError('');
  };

  // Abrir modal corrección (para ganadores ya verificados)
  const abrirCorreccion = (sorteo: Sorteo, ganador: NumeroGanador) => {
    setSorteoCorrigiendo(sorteo);
    setGanadorCorrigiendo(ganador);
    if (sorteo.tipo_id === 'FECHAS') {
      const [dia, mes] = ganador.num1.split('-');
      setCorreccionForm({
        num1: '', num2: '', num3: '',
        paga_reventado: ganador.paga_reventado || false,
        tipo_monazos: '',
        dia: dia,
        mes: MESES_FULL[parseInt(mes, 10) - 1] || '',
      });
    } else {
      setCorreccionForm({
        num1: ganador.num1,
        num2: ganador.num2 || '',
        num3: ganador.num3 || '',
        paga_reventado: ganador.paga_reventado || false,
        tipo_monazos: ganador.tipo_monazos || '',
        dia: '', mes: '',
      });
    }
    setCorreccionMotivo('');
    setCorreccionError('');
    setCorreccionOpen(true);
  };

  const cerrarCorreccion = () => {
    setCorreccionOpen(false);
    setGanadorCorrigiendo(null);
    setSorteoCorrigiendo(null);
    setCorreccionMotivo('');
    setCorreccionError('');
  };

  // Guardar corrección
  const guardarCorreccion = async () => {
    if (!ganadorCorrigiendo || !sorteoCorrigiendo) return;
    if (!correccionMotivo.trim()) {
      setCorreccionError('El motivo es obligatorio');
      return;
    }
    setCorreccionGuardando(true);
    setCorreccionError('');

    try {
      const tipo = sorteoCorrigiendo.tipo_id;
      const body: any = { motivo: correccionMotivo.trim() };

      switch (tipo) {
        case 'NORMAL':
          body.num1 = correccionForm.num1.padStart(2, '0');
          break;
        case 'REVENTADO':
          body.num1 = correccionForm.num1.padStart(2, '0');
          body.paga_reventado = correccionForm.paga_reventado;
          break;
        case 'MEGA':
          body.num1 = correccionForm.num1.padStart(2, '0');
          body.paga_reventado = correccionForm.paga_reventado;
          if (correccionForm.paga_reventado && correccionForm.num2) {
            body.num2 = correccionForm.num2.padStart(2, '0');
          }
          break;
        case 'PARLEY':
          body.num1 = correccionForm.num1.padStart(2, '0');
          body.num2 = correccionForm.num2.padStart(2, '0');
          body.num3 = correccionForm.num3.padStart(2, '0');
          break;
        case 'MONAZOS':
          body.num1 = correccionForm.num1;
          if (correccionForm.tipo_monazos) body.tipo_monazos = correccionForm.tipo_monazos;
          break;
        case 'FECHAS': {
          const mm = (MESES_FULL.indexOf(correccionForm.mes) + 1).toString().padStart(2, '0');
          body.num1 = `${correccionForm.dia.padStart(2, '0')}-${mm}`;
          break;
        }
      }

      await fetchAPI<any>(`/ganadores/${ganadorCorrigiendo.id}/corregir`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });

      cerrarCorreccion();
      cargarDatos();
    } catch (e: any) {
      setCorreccionError(e.message || 'Error al corregir');
    } finally {
      setCorreccionGuardando(false);
    }
  };

  // Guardar premio
  const guardarPremio = async () => {
    if (!sorteoSeleccionado) return;
    setGuardando(true);
    setError('');

    try {
      // Si estamos editando, eliminar el anterior primero
      if (modalMode === 'editar' && ganadorEditando) {
        await fetchAPI<any>(`/ganadores/${ganadorEditando.id}`, { method: 'DELETE' });
      }

      const tipo = sorteoSeleccionado.tipo_id;
      const body: any = {
        sorteo_id: sorteoSeleccionado.id,
        fecha: fechaFiltro,
        registrado_por: user?.id || 'BANCA',
      };

      // Construir payload segun tipo
      switch (tipo) {
        case 'NORMAL':
          body.num1 = formData.num1.padStart(2, '0');
          break;
        case 'REVENTADO':
          body.num1 = formData.num1.padStart(2, '0');
          body.paga_reventado = formData.paga_reventado;
          break;
        case 'MEGA':
          body.num1 = formData.num1.padStart(2, '0');
          body.paga_reventado = formData.paga_reventado;
          if (formData.paga_reventado && formData.num2) {
            body.num2 = formData.num2.padStart(2, '0');
          }
          break;
        case 'PARLEY':
          body.num1 = formData.num1.padStart(2, '0');
          body.num2 = formData.num2.padStart(2, '0');
          body.num3 = formData.num3.padStart(2, '0');
          break;
        case 'MONAZOS':
          body.num1 = formData.num1;
          if (formData.tipo_monazos) body.tipo_monazos = formData.tipo_monazos;
          break;
        case 'FECHAS': {
          const mm = (MESES_FULL.indexOf(formData.mes) + 1).toString().padStart(2, '0');
          body.num1 = `${formData.dia.padStart(2, '0')}-${mm}`;
          break;
        }
      }

      await fetchAPI<any>('/ganadores', {
        method: 'POST',
        body: JSON.stringify(body),
      });

      cerrarModal();
      cargarDatos();
    } catch (e: any) {
      setError(e.message || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  // Eliminar premio
  const eliminarPremio = async (ganadorId: number, verificado: boolean) => {
    let motivo = '';
    if (verificado) {
      const input = prompt('Este ganador ya tiene premios aplicados.\nIndique el motivo de la eliminación (obligatorio):');
      if (!input || !input.trim()) return;
      motivo = input.trim();
    } else {
      if (!confirm('¿Eliminar este premio?')) return;
    }
    try {
      const url = motivo
        ? `/ganadores/${ganadorId}?motivo=${encodeURIComponent(motivo)}`
        : `/ganadores/${ganadorId}`;
      await fetchAPI<any>(url, { method: 'DELETE' });
      cargarDatos();
    } catch (e: any) {
      setError(e.message);
    }
  };

  // Validar formulario
  const formularioValido = (): boolean => {
    if (!sorteoSeleccionado) return false;
    const tipo = sorteoSeleccionado.tipo_id;
    switch (tipo) {
      case 'NORMAL':
        return formData.num1.length >= 1 && formData.num1.length <= 2;
      case 'REVENTADO':
        return formData.num1.length >= 1 && formData.num1.length <= 2;
      case 'MEGA':
        if (formData.num1.length < 1 || formData.num1.length > 2) return false;
        if (formData.paga_reventado && (formData.num2.length < 1 || formData.num2.length > 2)) return false;
        return true;
      case 'PARLEY':
        return formData.num1.length >= 1 && formData.num1.length <= 2
          && formData.num2.length >= 1 && formData.num2.length <= 2
          && formData.num3.length >= 1 && formData.num3.length <= 2;
      case 'FECHAS': {
        const dia = parseInt(formData.dia, 10);
        return !!formData.dia && !!formData.mes && dia >= 1 && dia <= 31;
      }
      case 'MONAZOS':
        return formData.num1.length === 3;
      default:
        return false;
    }
  };

  // Renderizar formulario dinamico segun tipo
  const renderFormulario = () => {
    if (!sorteoSeleccionado) return null;
    const tipo = sorteoSeleccionado.tipo_id;

    return (
      <div className="space-y-4">
        <div className="p-3 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-500">Sorteo:</p>
          <p className="font-semibold">
            <span className={`inline-block px-2 py-0.5 rounded text-white text-xs mr-2 ${TIPOS_CONFIG[tipo]?.color || 'bg-gray-400'}`}>
              {TIPOS_CONFIG[tipo]?.label || tipo}
            </span>
            {sorteoSeleccionado.nombre}
          </p>
        </div>

        {/* NORMAL: solo numero 00-99 */}
        {tipo === 'NORMAL' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Numero Ganador (00-99)</label>
            <input
              type="text"
              value={formData.num1}
              onChange={(e) => setFormData({ ...formData, num1: e.target.value.replace(/\D/g, '').slice(0, 2) })}
              placeholder="00"
              maxLength={2}
              className="w-full px-4 py-3 text-center text-4xl font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        )}

        {/* REVENTADO: numero + SI/NO paga reventado */}
        {tipo === 'REVENTADO' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Numero Ganador (00-99)</label>
              <input
                type="text"
                value={formData.num1}
                onChange={(e) => setFormData({ ...formData, num1: e.target.value.replace(/\D/g, '').slice(0, 2) })}
                placeholder="00"
                maxLength={2}
                className="w-full px-4 py-3 text-center text-4xl font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">¿Paga Reventado?</label>
              <div className="flex gap-2">
                {[true, false].map(val => (
                  <button
                    key={String(val)}
                    type="button"
                    onClick={() => setFormData({ ...formData, paga_reventado: val })}
                    className={`flex-1 py-3 px-4 rounded-lg font-medium border-2 transition text-lg ${
                      formData.paga_reventado === val
                        ? val
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : 'border-red-500 bg-red-50 text-red-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {val ? 'SI' : 'NO'}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {/* MEGA: numero + SI/NO mega reventado + num2 si SI */}
        {tipo === 'MEGA' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Numero Ganador (00-99)</label>
              <input
                type="text"
                value={formData.num1}
                onChange={(e) => setFormData({ ...formData, num1: e.target.value.replace(/\D/g, '').slice(0, 2) })}
                placeholder="00"
                maxLength={2}
                className="w-full px-4 py-3 text-center text-4xl font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">¿Paga Mega Reventado?</label>
              <div className="flex gap-2">
                {[true, false].map(val => (
                  <button
                    key={String(val)}
                    type="button"
                    onClick={() => setFormData({ ...formData, paga_reventado: val, num2: val ? formData.num2 : '' })}
                    className={`flex-1 py-3 px-4 rounded-lg font-medium border-2 transition text-lg ${
                      formData.paga_reventado === val
                        ? val
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : 'border-red-500 bg-red-50 text-red-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {val ? 'SI' : 'NO'}
                  </button>
                ))}
              </div>
            </div>
            {formData.paga_reventado && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Numero Secundario (00-99)</label>
                <input
                  type="text"
                  value={formData.num2}
                  onChange={(e) => setFormData({ ...formData, num2: e.target.value.replace(/\D/g, '').slice(0, 2) })}
                  placeholder="00"
                  maxLength={2}
                  className="w-full px-4 py-3 text-center text-2xl font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400"
                />
              </div>
            )}
          </>
        )}

        {/* PARLEY: 3 numeros */}
        {tipo === 'PARLEY' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tres Numeros Ganadores (00-99)</label>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <p className="text-xs text-center text-gray-500 mb-1">Numero 1</p>
                <input
                  type="text"
                  value={formData.num1}
                  onChange={(e) => setFormData({ ...formData, num1: e.target.value.replace(/\D/g, '').slice(0, 2) })}
                  placeholder="00"
                  maxLength={2}
                  className="w-full px-4 py-3 text-center text-3xl font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                />
              </div>
              <div>
                <p className="text-xs text-center text-gray-500 mb-1">Numero 2</p>
                <input
                  type="text"
                  value={formData.num2}
                  onChange={(e) => setFormData({ ...formData, num2: e.target.value.replace(/\D/g, '').slice(0, 2) })}
                  placeholder="00"
                  maxLength={2}
                  className="w-full px-4 py-3 text-center text-3xl font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                />
              </div>
              <div>
                <p className="text-xs text-center text-gray-500 mb-1">Numero 3</p>
                <input
                  type="text"
                  value={formData.num3}
                  onChange={(e) => setFormData({ ...formData, num3: e.target.value.replace(/\D/g, '').slice(0, 2) })}
                  placeholder="00"
                  maxLength={2}
                  className="w-full px-4 py-3 text-center text-3xl font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                />
              </div>
            </div>
          </div>
        )}

        {/* FECHAS: dia + mes */}
        {tipo === 'FECHAS' && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Dia</label>
              <input
                type="number"
                value={formData.dia}
                onChange={(e) => setFormData({ ...formData, dia: e.target.value })}
                placeholder="15"
                min={1}
                max={31}
                className="w-full px-4 py-3 text-center text-2xl font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mes</label>
              <select
                value={formData.mes}
                onChange={(e) => setFormData({ ...formData, mes: e.target.value })}
                className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="">Seleccione</option>
                {MESES_FULL.map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* MONAZOS: numero 3 digitos */}
        {tipo === 'MONAZOS' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Numero Ganador (3 digitos)</label>
            <input
              type="text"
              value={formData.num1}
              onChange={(e) => setFormData({ ...formData, num1: e.target.value.replace(/\D/g, '').slice(0, 3) })}
              placeholder="123"
              maxLength={3}
              className="w-full px-4 py-3 text-center text-4xl font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Premios</h1>
          <p className="text-gray-500">Registro de premios por sorteo</p>
        </div>
        <button
          onClick={cargarDatos}
          className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
          title="Refrescar"
        >
          <RefreshCw size={20} />
        </button>
      </div>

      {/* Error */}
      {error && !modalOpen && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Filtros */}
      <Card>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <Input
              type="date"
              value={fechaFiltro}
              onChange={(e) => setFechaFiltro(e.target.value)}
              leftIcon={<Calendar size={18} />}
            />
          </div>
          <select
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={tipoFiltro}
            onChange={(e) => setTipoFiltro(e.target.value)}
          >
            <option value="">Todos los tipos</option>
            {Object.entries(TIPOS_CONFIG).map(([id, cfg]) => (
              <option key={id} value={id}>{cfg.label}</option>
            ))}
          </select>
        </div>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <p className="text-sm text-gray-500">Total Sorteos</p>
          <p className="text-2xl font-bold text-gray-900">{totalSorteos}</p>
        </Card>
        <Card>
          <p className="text-sm text-gray-500">Con Premio</p>
          <p className="text-2xl font-bold text-green-600">{conPremio}</p>
        </Card>
        <Card>
          <p className="text-sm text-gray-500">Sin Premio</p>
          <p className="text-2xl font-bold text-red-600">{sinPremio}</p>
        </Card>
      </div>

      {/* Alerta: sorteos de otros dias sin ganador */}
      {pendientes.length > 0 && (
        <div className="p-4 bg-amber-50 border border-amber-300 rounded-lg">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="text-amber-600" size={20} />
            <h3 className="font-semibold text-amber-800">
              {pendientes.length} sorteo{pendientes.length > 1 ? 's' : ''} sin ganador asignado
            </h3>
          </div>
          <div className="space-y-2">
            {pendientes.map(p => (
              <div key={`${p.id}-${p.fecha}`} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-white rounded-lg px-4 py-3 border border-amber-200">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className={`inline-block px-2 py-0.5 rounded text-white text-xs ${TIPOS_CONFIG[p.tipo_id]?.color || 'bg-gray-400'}`}>
                    {TIPOS_CONFIG[p.tipo_id]?.label || p.tipo_id}
                  </span>
                  <span className="font-medium text-gray-900">{p.nombre}</span>
                  <span className="text-sm text-gray-500">{p.fecha}</span>
                </div>
                <button
                  onClick={() => {
                    setFechaFiltro(p.fecha);
                  }}
                  className="w-full sm:w-auto px-3 py-2 sm:py-1.5 text-sm font-medium text-amber-700 bg-amber-100 hover:bg-amber-200 rounded-lg transition text-center"
                >
                  Ir a fecha
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Lista de sorteos con premios */}
      <Card padding="none">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Cargando...</div>
        ) : sorteosConPremio.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No hay sorteos cerrados para esta fecha
          </div>
        ) : (
          <div className="divide-y">
            {sorteosConPremio.map(({ sorteo, ganador }) => (
              <div key={sorteo.id} className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:bg-gray-50">
                <div className="flex items-center gap-4">
                  <div className={`w-2 h-12 rounded-full ${TIPOS_CONFIG[sorteo.tipo_id]?.color || 'bg-gray-400'}`} />
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-block px-2 py-0.5 rounded text-white text-xs ${TIPOS_CONFIG[sorteo.tipo_id]?.color || 'bg-gray-400'}`}>
                        {TIPOS_CONFIG[sorteo.tipo_id]?.label || sorteo.tipo_id}
                      </span>
                      <span className="font-medium text-gray-900">{sorteo.nombre}</span>
                    </div>
                    <p className="text-sm text-gray-500">Cierra: {sorteo.hora_cierre}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 flex-wrap">
                  {/* Estado del sorteo: para fechas pasadas, mostrar CERRADO */}
                  {(() => {
                    const estadoMostrar = fechaFiltro < fechaLocalYMD() ? 'CERRADO' : sorteo.estado;
                    return (
                      <Badge variant={estadoMostrar === 'CERRADO' ? 'danger' : 'neutral'}>
                        {estadoMostrar}
                      </Badge>
                    );
                  })()}

                  {/* Premio */}
                  {ganador ? (
                    <div className="text-right sm:min-w-[160px]">
                      <div className="flex items-center gap-1 justify-end">
                        <Trophy className="text-yellow-500" size={16} />
                        <span className="font-mono font-bold text-lg text-blue-600">
                          {formatearPremio(ganador)}
                        </span>
                      </div>
                      {ganador.verificado && (
                        <Badge variant="success" size="sm">Bloqueado</Badge>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-400 italic sm:min-w-[160px] text-right">Sin premio</span>
                  )}

                  {/* Acciones */}
                  <div className="flex gap-1 sm:min-w-[120px] justify-end">
                    {!ganador && (
                      <button
                        onClick={() => abrirModalAsignar(sorteo)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                        title="Asignar premio"
                      >
                        <Gift size={18} />
                      </button>
                    )}
                    {ganador && !ganador.verificado && (
                      <>
                        <button
                          onClick={() => abrirModalEditar(sorteo, ganador)}
                          className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                          title="Editar"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => eliminarPremio(ganador.id, false)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded"
                          title="Eliminar"
                        >
                          <Trash2 size={18} />
                        </button>
                      </>
                    )}
                    {ganador && ganador.verificado && user?.role === 'BANCA' && (
                      <>
                        <button
                          onClick={() => abrirCorreccion(sorteo, ganador)}
                          className="p-2 text-amber-600 hover:bg-amber-50 rounded"
                          title="Corregir número ganador"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => eliminarPremio(ganador.id, true)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded"
                          title="Eliminar ganador"
                        >
                          <Trash2 size={18} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Modal asignar/editar */}
      <Modal
        isOpen={modalOpen}
        onClose={cerrarModal}
        title={modalMode === 'asignar' ? 'Asignar Premio' : 'Editar Premio'}
      >
        <div className="space-y-4">
          {renderFormulario()}

          {error && (
            <div className="p-2 bg-red-50 text-red-700 text-sm rounded">
              {error}
            </div>
          )}

          <div className="flex gap-2 justify-end pt-4 border-t">
            <Button variant="outline" onClick={cerrarModal}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              leftIcon={<Trophy size={18} />}
              onClick={guardarPremio}
              disabled={guardando || !formularioValido()}
            >
              {guardando ? 'Guardando...' : modalMode === 'asignar' ? 'Asignar Premio' : 'Guardar Cambios'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal corrección de número ganador */}
      <Modal
        isOpen={correccionOpen}
        onClose={cerrarCorreccion}
        title="Corregir Número Ganador"
      >
        <div className="space-y-4">
          {/* Warning */}
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
            <AlertTriangle className="text-amber-600 mt-0.5 flex-shrink-0" size={18} />
            <div className="text-sm text-amber-800">
              <p className="font-semibold">Corrección de error humano</p>
              <p>Esta acción NO recalcula premios automáticamente. Si ya se aplicaron premios, deberá recalcularlos manualmente después de la corrección.</p>
            </div>
          </div>

          {/* Info del sorteo */}
          {sorteoCorrigiendo && (
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Sorteo:</p>
              <p className="font-semibold">
                <span className={`inline-block px-2 py-0.5 rounded text-white text-xs mr-2 ${TIPOS_CONFIG[sorteoCorrigiendo.tipo_id]?.color || 'bg-gray-400'}`}>
                  {TIPOS_CONFIG[sorteoCorrigiendo.tipo_id]?.label || sorteoCorrigiendo.tipo_id}
                </span>
                {sorteoCorrigiendo.nombre}
              </p>
              {ganadorCorrigiendo && (
                <p className="text-sm text-gray-500 mt-1">
                  Valor actual: <span className="font-mono font-bold text-blue-600">{formatearPremio(ganadorCorrigiendo)}</span>
                </p>
              )}
            </div>
          )}

          {/* Formulario según tipo */}
          {sorteoCorrigiendo && (() => {
            const tipo = sorteoCorrigiendo.tipo_id;
            return (
              <div className="space-y-4">
                {tipo === 'NORMAL' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Número Corregido (00-99)</label>
                    <input type="text" value={correccionForm.num1}
                      onChange={(e) => setCorreccionForm({ ...correccionForm, num1: e.target.value.replace(/\D/g, '').slice(0, 2) })}
                      placeholder="00" maxLength={2}
                      className="w-full px-4 py-3 text-center text-4xl font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500" />
                  </div>
                )}
                {tipo === 'REVENTADO' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Número Corregido (00-99)</label>
                      <input type="text" value={correccionForm.num1}
                        onChange={(e) => setCorreccionForm({ ...correccionForm, num1: e.target.value.replace(/\D/g, '').slice(0, 2) })}
                        placeholder="00" maxLength={2}
                        className="w-full px-4 py-3 text-center text-4xl font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">¿Paga Reventado?</label>
                      <div className="flex gap-2">
                        {[true, false].map(val => (
                          <button key={String(val)} type="button"
                            onClick={() => setCorreccionForm({ ...correccionForm, paga_reventado: val })}
                            className={`flex-1 py-3 px-4 rounded-lg font-medium border-2 transition text-lg ${
                              correccionForm.paga_reventado === val
                                ? val ? 'border-green-500 bg-green-50 text-green-700' : 'border-red-500 bg-red-50 text-red-700'
                                : 'border-gray-300 hover:border-gray-400'
                            }`}>{val ? 'SI' : 'NO'}</button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
                {tipo === 'MEGA' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Número Corregido (00-99)</label>
                      <input type="text" value={correccionForm.num1}
                        onChange={(e) => setCorreccionForm({ ...correccionForm, num1: e.target.value.replace(/\D/g, '').slice(0, 2) })}
                        placeholder="00" maxLength={2}
                        className="w-full px-4 py-3 text-center text-4xl font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">¿Paga Mega Reventado?</label>
                      <div className="flex gap-2">
                        {[true, false].map(val => (
                          <button key={String(val)} type="button"
                            onClick={() => setCorreccionForm({ ...correccionForm, paga_reventado: val, num2: val ? correccionForm.num2 : '' })}
                            className={`flex-1 py-3 px-4 rounded-lg font-medium border-2 transition text-lg ${
                              correccionForm.paga_reventado === val
                                ? val ? 'border-green-500 bg-green-50 text-green-700' : 'border-red-500 bg-red-50 text-red-700'
                                : 'border-gray-300 hover:border-gray-400'
                            }`}>{val ? 'SI' : 'NO'}</button>
                        ))}
                      </div>
                    </div>
                    {correccionForm.paga_reventado && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Número Secundario (00-99)</label>
                        <input type="text" value={correccionForm.num2}
                          onChange={(e) => setCorreccionForm({ ...correccionForm, num2: e.target.value.replace(/\D/g, '').slice(0, 2) })}
                          placeholder="00" maxLength={2}
                          className="w-full px-4 py-3 text-center text-2xl font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400" />
                      </div>
                    )}
                  </>
                )}
                {tipo === 'PARLEY' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Tres Números Corregidos (00-99)</label>
                    <div className="grid grid-cols-3 gap-3">
                      {(['num1', 'num2', 'num3'] as const).map((key, i) => (
                        <div key={key}>
                          <p className="text-xs text-center text-gray-500 mb-1">Número {i + 1}</p>
                          <input type="text" value={correccionForm[key]}
                            onChange={(e) => setCorreccionForm({ ...correccionForm, [key]: e.target.value.replace(/\D/g, '').slice(0, 2) })}
                            placeholder="00" maxLength={2}
                            className="w-full px-4 py-3 text-center text-3xl font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {tipo === 'FECHAS' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Día</label>
                      <input type="number" value={correccionForm.dia}
                        onChange={(e) => setCorreccionForm({ ...correccionForm, dia: e.target.value })}
                        placeholder="15" min={1} max={31}
                        className="w-full px-4 py-3 text-center text-2xl font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Mes</label>
                      <select value={correccionForm.mes}
                        onChange={(e) => setCorreccionForm({ ...correccionForm, mes: e.target.value })}
                        className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500">
                        <option value="">Seleccione</option>
                        {MESES_FULL.map(m => (<option key={m} value={m}>{m}</option>))}
                      </select>
                    </div>
                  </div>
                )}
                {tipo === 'MONAZOS' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Número Corregido (3 dígitos)</label>
                    <input type="text" value={correccionForm.num1}
                      onChange={(e) => setCorreccionForm({ ...correccionForm, num1: e.target.value.replace(/\D/g, '').slice(0, 3) })}
                      placeholder="123" maxLength={3}
                      className="w-full px-4 py-3 text-center text-4xl font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500" />
                  </div>
                )}
              </div>
            );
          })()}

          {/* Motivo obligatorio */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Motivo de la corrección <span className="text-red-500">*</span>
            </label>
            <textarea
              value={correccionMotivo}
              onChange={(e) => setCorreccionMotivo(e.target.value)}
              placeholder="Describa por qué se corrige el número ganador..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
            />
          </div>

          {correccionError && (
            <div className="p-2 bg-red-50 text-red-700 text-sm rounded">
              {correccionError}
            </div>
          )}

          <div className="flex gap-2 justify-end pt-4 border-t">
            <Button variant="outline" onClick={cerrarCorreccion}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              leftIcon={<Edit size={18} />}
              onClick={guardarCorreccion}
              disabled={correccionGuardando || !correccionMotivo.trim()}
            >
              {correccionGuardando ? 'Corrigiendo...' : 'Corregir Número'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};


// ========================
// TAB 2: REGLAS DE PREMIOS
// ========================
const ReglasPremiosTab: React.FC = () => {
  // Config
  const [sorteos, setSorteos] = useState<SorteoSimple[]>([]);
  const [sorteoSeleccionado, setSorteoSeleccionado] = useState('');
  const [locales, setLocales] = useState<LocalSimple[]>([]);
  const [conceptosPorTipo, setConceptosPorTipo] = useState<Record<string, string[]>>({});

  // Rules
  const [alcance, setAlcance] = useState<'global' | 'local'>('global');
  const [localSeleccionado, setLocalSeleccionado] = useState('');
  const [reglasGlobales, setReglasGlobales] = useState<ReglaPremio[]>([]);
  const [reglasLocales, setReglasLocales] = useState<ReglaPremio[]>([]);
  const [localesConReglas, setLocalesConReglas] = useState<string[]>([]);
  const [multiplicadores, setMultiplicadores] = useState<Record<string, string>>({});
  const [batchSorteos, setBatchSorteos] = useState<string[]>([]);
  const [batchAlcance, setBatchAlcance] = useState<'global' | 'local'>('global');
  const [batchLocales, setBatchLocales] = useState<string[]>([]);
  const [batchMultiplicadores, setBatchMultiplicadores] = useState<Record<string, string>>({});
  const [guardandoBatch, setGuardandoBatch] = useState(false);

  // UI
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');


  // Derived
  const sorteoActual = sorteos.find(s => s.id === sorteoSeleccionado);
  const tipoDelSorteo = sorteoActual?.tipo_id || '';
  const conceptos = useMemo(() => conceptosPorTipo[tipoDelSorteo] || [], [conceptosPorTipo, tipoDelSorteo]);
  const sorteosDelTipo = useMemo(
    () => sorteos.filter(s => s.tipo_id === tipoDelSorteo),
    [sorteos, tipoDelSorteo]
  );

  // Init: cargar sorteos, conceptos y locales
  useEffect(() => {
    const init = async () => {
      try {
        const [conceptosData, locsData, sorteosData] = await Promise.all([
          fetchAPI<Record<string, string[]>>('/reglas-premio/conceptos'),
          fetchAPI<LocalSimple[]>('/reglas-premio/locales'),
          fetchAPI<SorteoSimple[]>('/reglas-premio/sorteos'),
        ]);
        setConceptosPorTipo(conceptosData);
        setLocales(locsData);
        setSorteos(sorteosData);
        if (sorteosData.length > 0) {
          setSorteoSeleccionado(sorteosData[0].id);
        }
      } catch (e: any) {
        setError(e.message || 'Error cargando datos iniciales');
      }
    };
    init();
  }, []);

  // Cargar reglas al cambiar sorteo/alcance/local
  const cargarReglas = useCallback(async () => {
    const sorteoNombre = sorteos.find(s => s.id === sorteoSeleccionado)?.nombre;
    if (!sorteoNombre) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    setMensaje('');
    try {
      // Siempre cargar globales
      const globales = await fetchAPI<ReglaPremio[]>(
        `/reglas-premio?sorteo_nombre=${encodeURIComponent(sorteoNombre)}`
      );
      setReglasGlobales(globales);

      // Locales con reglas personalizadas
      const locConReglas = await fetchAPI<string[]>(
        `/reglas-premio/locales-con-reglas?sorteo_nombre=${encodeURIComponent(sorteoNombre)}`
      );
      setLocalesConReglas(locConReglas);

      // Si modo local, cargar reglas del local
      if (alcance === 'local' && localSeleccionado) {
        const locData = await fetchAPI<ReglaPremio[]>(
          `/reglas-premio?sorteo_nombre=${encodeURIComponent(sorteoNombre)}&local_id=${localSeleccionado}`
        );
        setReglasLocales(locData);
      } else {
        setReglasLocales([]);
      }
    } catch (e: any) {
      setError(e.message || 'Error cargando reglas');
    } finally {
      setLoading(false);
    }
  }, [sorteoSeleccionado, alcance, localSeleccionado, sorteos]);

  useEffect(() => {
    cargarReglas();
  }, [cargarReglas]);

  // Sincronizar multiplicadores con reglas cargadas
  useEffect(() => {
    const mults: Record<string, string> = {};
    if (alcance === 'global') {
      for (const c of conceptos) {
        const regla = reglasGlobales.find(r => r.concepto === c);
        mults[c] = regla ? String(regla.multiplicador) : '';
      }
    } else {
      for (const c of conceptos) {
        const reglaLocal = reglasLocales.find(r => r.concepto === c);
        mults[c] = reglaLocal ? String(reglaLocal.multiplicador) : '';
      }
    }
    setMultiplicadores(mults);
  }, [reglasGlobales, reglasLocales, sorteoSeleccionado, alcance, conceptos]);

  useEffect(() => {
    const inicial: Record<string, string> = {};
    for (const c of conceptos) inicial[c] = '';
    setBatchMultiplicadores(inicial);
  }, [conceptos]);

  useEffect(() => {
    if (sorteoActual?.id) {
      setBatchSorteos([sorteoActual.id]);
    } else {
      setBatchSorteos([]);
    }
    setBatchAlcance('global');
    setBatchLocales([]);
  }, [sorteoActual?.id]);


  const getMultGlobal = (concepto: string): number | null => {
    const regla = reglasGlobales.find(r => r.concepto === concepto);
    return regla ? Number(regla.multiplicador) : null;
  };

  // Guardar reglas (enteros)
  const guardarReglas = async () => {
    const reglasAGuardar = conceptos.filter(c => {
      const val = multiplicadores[c];
      return val && parseInt(val, 10) > 0;
    });
    if (reglasAGuardar.length === 0) {
      setError('Ingrese al menos un multiplicador');
      return;
    }
    setGuardando(true);
    setError('');
    setMensaje('');
    try {
      if (!sorteoActual?.id) {
        setError('Seleccione un sorteo valido');
        return;
      }
      if (alcance === 'local' && !localSeleccionado) {
        setError('Seleccione un local');
        return;
      }
      await fetchAPI<any>('/reglas-premio/batch', {
        method: 'POST',
        body: JSON.stringify({
          sorteos: [sorteoActual.id],
          items: reglasAGuardar.map((concepto) => ({
            concepto,
            multiplicador: parseInt(multiplicadores[concepto], 10),
          })),
          local_ids: alcance === 'local' ? [localSeleccionado] : undefined,
        }),
      });
      setMensaje('Reglas guardadas correctamente');
      cargarReglas();
    } catch (e: any) {
      setError(e.message || 'Error al guardar reglas');
    } finally {
      setGuardando(false);
    }
  };

  const aplicarLoteReglas = async () => {
    if (batchSorteos.length === 0) {
      setError('Seleccione al menos un sorteo para lote');
      return;
    }

    const items = conceptos
      .filter((c) => batchMultiplicadores[c] && parseInt(batchMultiplicadores[c], 10) > 0)
      .map((c) => ({ concepto: c, multiplicador: parseInt(batchMultiplicadores[c], 10) }));

    if (items.length === 0) {
      setError('Ingrese al menos un multiplicador para aplicar en lote');
      return;
    }

    if (batchAlcance === 'local' && batchLocales.length === 0) {
      setError('Seleccione al menos un local para el lote');
      return;
    }

    setGuardandoBatch(true);
    setError('');
    setMensaje('');
    try {
      await fetchAPI<any>('/reglas-premio/batch', {
        method: 'POST',
        body: JSON.stringify({
          sorteos: batchSorteos,
          items,
          local_ids: batchAlcance === 'local' ? batchLocales : undefined,
        }),
      });
      setMensaje('Lote aplicado correctamente');
      cargarReglas();
    } catch (e: any) {
      setError(e.message || 'Error aplicando lote');
    } finally {
      setGuardandoBatch(false);
    }
  };

  const eliminarReglaLocal = async (reglaId: number) => {
    try {
      await fetchAPI<any>(`/reglas-premio/${reglaId}`, { method: 'DELETE' });
      cargarReglas();
    } catch (e: any) {
      setError(e.message);
    }
  };


  const seleccionarLocal = (localId: string) => {
    setAlcance('local');
    setLocalSeleccionado(localId);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Reglas de Premios</h1>
        <p className="text-gray-500">Configuracion de multiplicadores por sorteo</p>
      </div>

      {/* Controles */}
      <Card>
        <div className="flex flex-col md:flex-row gap-4">
          {/* Sorteo */}
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Sorteo</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={sorteoSeleccionado}
              onChange={(e) => { setSorteoSeleccionado(e.target.value); setAlcance('global'); setLocalSeleccionado(''); }}
            >
              <option value="">Seleccione un sorteo</option>
              {sorteos.map(s => (
                <option key={s.id} value={s.id}>
                  {s.nombre} ({TIPO_LABELS[s.tipo_id] || s.tipo_id})
                </option>
              ))}
            </select>
          </div>

          {/* Alcance */}
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Alcance</label>
            <div className="flex border border-gray-300 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => { setAlcance('global'); setLocalSeleccionado(''); }}
                className={`flex-1 py-2 px-4 text-sm font-medium transition ${
                  alcance === 'global' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Global (Banca)
              </button>
              <button
                type="button"
                onClick={() => setAlcance('local')}
                className={`flex-1 py-2 px-4 text-sm font-medium transition ${
                  alcance === 'local' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Por Local
              </button>
            </div>
          </div>

          {/* Selector de local */}
          {alcance === 'local' && (
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Local</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={localSeleccionado}
                onChange={(e) => setLocalSeleccionado(e.target.value)}
              >
                <option value="">Seleccione un local</option>
                {locales.map(l => (
                  <option key={l.id} value={l.id}>
                    {l.nombre} {localesConReglas.includes(l.id) ? '(Personalizado)' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </Card>

      {/* Mensajes */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>
      )}
      {mensaje && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700">{mensaje}</div>
      )}

      {/* Lote de reglas */}
      {sorteoActual && (
        <Card>
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Aplicar reglas por lote ({TIPO_LABELS[tipoDelSorteo] || tipoDelSorteo})</h3>
              <p className="text-xs text-gray-500">Aplica conceptos y multiplicadores a multiples sorteos del mismo tipo</p>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Sorteos destino</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-40 overflow-auto pr-1">
                {sorteosDelTipo.map((s) => {
                  const activo = batchSorteos.includes(s.id);
                  return (
                    <label key={s.id} className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={activo}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setBatchSorteos([...batchSorteos, s.id]);
                          } else {
                            setBatchSorteos(batchSorteos.filter((id) => id !== s.id));
                          }
                        }}
                      />
                      <span className="text-sm text-gray-800">{s.nombre}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Alcance del lote</p>
                <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={() => { setBatchAlcance('global'); setBatchLocales([]); }}
                    className={`flex-1 py-2 px-3 text-sm font-medium ${batchAlcance === 'global' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                  >
                    Global
                  </button>
                  <button
                    type="button"
                    onClick={() => setBatchAlcance('local')}
                    className={`flex-1 py-2 px-3 text-sm font-medium ${batchAlcance === 'local' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                  >
                    Por Local
                  </button>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Locales destino</p>
                <div className={`grid grid-cols-1 gap-2 max-h-28 overflow-auto pr-1 ${batchAlcance === 'global' ? 'opacity-60 pointer-events-none' : ''}`}>
                  {locales.map((l) => {
                    const activo = batchLocales.includes(l.id);
                    return (
                      <label key={l.id} className="flex items-center gap-2 text-sm text-gray-800">
                        <input
                          type="checkbox"
                          checked={activo}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setBatchLocales([...batchLocales, l.id]);
                            } else {
                              setBatchLocales(batchLocales.filter((id) => id !== l.id));
                            }
                          }}
                        />
                        {l.nombre}
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Multiplicadores del lote</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {conceptos.map((concepto) => (
                  <div key={concepto} className="border rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">{CONCEPTO_LABELS[concepto] || concepto}</p>
                    <Input
                      type="number"
                      min={0}
                      step={1}
                      value={batchMultiplicadores[concepto] || ''}
                      onChange={(e) => setBatchMultiplicadores({
                        ...batchMultiplicadores,
                        [concepto]: e.target.value.replace(/[^0-9]/g, ''),
                      })}
                      placeholder="0"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                variant="secondary"
                leftIcon={<Save size={16} />}
                onClick={aplicarLoteReglas}
                disabled={guardandoBatch}
              >
                {guardandoBatch ? 'Aplicando lote...' : 'Aplicar Lote'}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Tabla de reglas */}
      {!sorteoSeleccionado ? (
        <Card>
          <div className="p-8 text-center text-gray-500">Seleccione un sorteo para configurar sus reglas</div>
        </Card>
      ) : loading ? (
        <div className="p-8 text-center text-gray-500">Cargando...</div>
      ) : alcance === 'local' && !localSeleccionado ? (
        <Card>
          <div className="p-8 text-center text-gray-500">Seleccione un local para configurar sus reglas</div>
        </Card>
      ) : (
        <Card padding="none">
          {/* Info del sorteo */}
          <div className="px-4 py-3 border-b bg-gray-50 flex items-center gap-2">
            <span className={`inline-block px-2 py-0.5 rounded text-white text-xs ${TIPOS_CONFIG[tipoDelSorteo]?.color || 'bg-gray-400'}`}>
              {TIPO_LABELS[tipoDelSorteo] || tipoDelSorteo}
            </span>
            <span className="font-medium text-gray-900">{sorteoActual?.nombre}</span>
          </div>

          <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Concepto</th>
                {alcance === 'local' && (
                  <th className="text-center px-4 py-3 text-sm font-medium text-gray-700">Global</th>
                )}
                <th className="text-center px-4 py-3 text-sm font-medium text-gray-700">
                  Multiplicador {alcance === 'local' ? '(Local)' : ''}
                </th>
                {alcance === 'local' && (
                  <th className="text-center px-4 py-3 text-sm font-medium text-gray-700 w-20">Acciones</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y">
              {conceptos.map(concepto => {
                const globalVal = getMultGlobal(concepto);
                const reglaLocal = reglasLocales.find(r => r.concepto === concepto);

                return (
                  <tr key={concepto} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className="font-medium text-gray-900">
                        {CONCEPTO_LABELS[concepto] || concepto}
                      </span>
                    </td>
                    {alcance === 'local' && (
                      <td className="px-4 py-3 text-center">
                        <span className="text-gray-600 font-mono">
                          {globalVal !== null ? `x${globalVal}` : '-'}
                        </span>
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <span className="text-gray-500 text-lg">x</span>
                        <input
                          type="number"
                          value={multiplicadores[concepto] || ''}
                          onChange={(e) => setMultiplicadores({
                            ...multiplicadores,
                            [concepto]: e.target.value.replace(/[^0-9]/g, ''),
                          })}
                          placeholder="0"
                          min={0}
                          step={1}
                          className="w-28 px-3 py-2 text-center font-mono text-lg border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </td>
                    {alcance === 'local' && (
                      <td className="px-4 py-3 text-center">
                        {reglaLocal && (
                          <button
                            onClick={() => eliminarReglaLocal(reglaLocal.id)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                            title="Eliminar regla local"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>

          {/* Boton guardar */}
          <div className="px-4 py-3 border-t bg-gray-50 flex justify-end">
            <Button
              variant="primary"
              leftIcon={<Save size={18} />}
              onClick={guardarReglas}
              disabled={guardando}
            >
              {guardando ? 'Guardando...' : 'Guardar Reglas'}
            </Button>
          </div>
        </Card>
      )}

      {/* Vista de locales con reglas personalizadas */}
      {alcance === 'global' && localesConReglas.length > 0 && (
        <Card>
          <h3 className="text-sm font-medium text-gray-700 mb-3">Locales con reglas personalizadas</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {locales.filter(l => localesConReglas.includes(l.id)).map(l => (
              <button
                key={l.id}
                onClick={() => seleccionarLocal(l.id)}
                className="p-3 rounded-lg border border-blue-200 bg-blue-50 hover:border-blue-300 text-left transition hover:shadow-sm"
              >
                <p className="font-medium text-sm text-gray-900">{l.nombre}</p>
                <span className="text-xs text-blue-600">Personalizado</span>
              </button>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};


// ========================
// TAB 3: PREMIOS POR LOCAL
// ========================

interface JugadaGanadora {
  jugada_id: string;
  ticket_id: string;
  ticket_number: string | null;
  vendedor_id: string;
  num1: string;
  num2: string | null;
  concepto: string;
  monto_jugado: string;
  multiplicador: number;
  pago: string;
  regla_origen: string;
}

interface PremioLocal {
  local_id: string;
  local_nombre: string;
  total_local: string;
  jugadas_ganadoras: JugadaGanadora[];
  warnings: string[];
}

interface PremioSorteo {
  sorteo_id: string;
  sorteo_nombre: string;
  tipo_id: string;
  ganador_num1: string;
  ganador_num2: string | null;
  ganador_num3: string | null;
  ganador_paga_reventado: boolean | null;
  ganador_verificado: boolean;
  total_sorteo: string;
  total_ventas_sorteo: string;
  balance_sorteo: string;
  locales: PremioLocal[];
}

interface PremiosPorLocalResponse {
  fecha: string;
  total_global: string;
  total_ventas_global: string;
  balance_global: string;
  sorteos: PremioSorteo[];
  sorteos_sin_ganador: string[];
}

const REGLA_BADGE: Record<string, { label: string; variant: 'success' | 'neutral' | 'warning' | 'danger' }> = {
  CONGELADA: { label: 'Congelada', variant: 'success' },
  CONGELADA_GLOBAL: { label: 'Congelada', variant: 'success' },
  GLOBAL: { label: 'Global', variant: 'neutral' },
  LOCAL: { label: 'Local', variant: 'warning' },
  SIN_REGLA: { label: 'Sin Regla', variant: 'danger' },
};

const formatMonto = (val: string) => {
  const n = Number(val);
  if (isNaN(n)) return val;
  return n.toLocaleString('es-CR', { minimumFractionDigits: 0 });
};

const PremiosPorLocalTab: React.FC = () => {
  const [fechaFiltro, setFechaFiltro] = useState(fechaLocalYMD());
  const [data, setData] = useState<PremiosPorLocalResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedSorteos, setExpandedSorteos] = useState<Set<string>>(new Set());
  const [expandedLocales, setExpandedLocales] = useState<Set<string>>(new Set());

  const cargarDatos = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await fetchAPI<PremiosPorLocalResponse>(
        `/premios/por-local?fecha=${fechaFiltro}&incluir_no_verificados=true`
      );
      setData(result);
      setExpandedSorteos(new Set(result.sorteos.map(s => s.sorteo_id)));
      setExpandedLocales(new Set());
    } catch (e: any) {
      setError(e.message || 'Error al cargar premios');
    } finally {
      setLoading(false);
    }
  }, [fechaFiltro]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  const toggleSorteo = (id: string) => {
    setExpandedSorteos(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleLocal = (key: string) => {
    setExpandedLocales(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Premios por Local</h1>
          <p className="text-gray-500">Calculo de premios por local y sorteo</p>
        </div>
        <button
          onClick={cargarDatos}
          className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
          title="Refrescar"
        >
          <RefreshCw size={20} />
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>
      )}

      {/* Filtro fecha */}
      <Card>
        <div className="flex-1 max-w-xs">
          <Input
            type="date"
            value={fechaFiltro}
            onChange={(e) => setFechaFiltro(e.target.value)}
            leftIcon={<Calendar size={18} />}
          />
        </div>
      </Card>

      {/* Stats */}
      {data && (() => {
        const balanceNum = Number(data.balance_global);
        const esPositivo = balanceNum >= 0;
        return (
          <div className="space-y-4">
            {/* Fila principal: Ventas, Premios, Balance */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <TrendingUp size={20} className="text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Ventas</p>
                    <p className="text-2xl font-bold text-green-600">{formatMonto(data.total_ventas_global)}</p>
                  </div>
                </div>
              </Card>
              <Card>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <DollarSign size={20} className="text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Premios</p>
                    <p className="text-2xl font-bold text-red-600">{formatMonto(data.total_global)}</p>
                  </div>
                </div>
              </Card>
              <Card>
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${esPositivo ? 'bg-green-100' : 'bg-red-100'}`}>
                    {esPositivo
                      ? <TrendingUp size={20} className="text-green-600" />
                      : <TrendingDown size={20} className="text-red-600" />}
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Balance (Ventas - Premios)</p>
                    <p className={`text-2xl font-bold ${esPositivo ? 'text-green-600' : 'text-red-600'}`}>
                      {formatMonto(data.balance_global)}
                    </p>
                  </div>
                </div>
              </Card>
            </div>
            {/* Fila secundaria: conteos */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Trophy size={20} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Sorteos con Premios</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {data.sorteos.filter(s => Number(s.total_sorteo) > 0).length}
                    </p>
                  </div>
                </div>
              </Card>
              <Card>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <Gift size={20} className="text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Sin Ganador</p>
                    <p className="text-2xl font-bold text-yellow-600">{data.sorteos_sin_ganador.length}</p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        );
      })()}

      {/* Loading */}
      {loading && (
        <div className="text-center py-8 text-gray-500">Calculando premios...</div>
      )}

      {/* Sin datos */}
      {!loading && data && data.sorteos.length === 0 && (
        <Card>
          <div className="text-center py-8 text-gray-500">
            No hay premios calculados para esta fecha
          </div>
        </Card>
      )}

      {/* Lista por sorteo */}
      {!loading && data && data.sorteos.map(sorteo => {
        const isExpanded = expandedSorteos.has(sorteo.sorteo_id);
        const tipoCfg = TIPOS_CONFIG[sorteo.tipo_id] || { label: sorteo.tipo_id, color: 'bg-gray-600' };
        const balanceSorteo = Number(sorteo.balance_sorteo);
        const esSorteoPositivo = balanceSorteo >= 0;

        return (
          <Card key={sorteo.sorteo_id}>
            {/* Header sorteo */}
            <button
              onClick={() => toggleSorteo(sorteo.sorteo_id)}
              className="w-full flex items-center justify-between py-1"
            >
              <div className="flex items-center gap-3">
                {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                <span className={`px-2 py-0.5 rounded text-xs text-white font-medium ${tipoCfg.color}`}>
                  {tipoCfg.label}
                </span>
                <span className="font-semibold text-gray-900">{sorteo.sorteo_nombre}</span>
                <span className="text-gray-500 text-sm">#{sorteo.ganador_num1}</span>
                {!sorteo.ganador_verificado && (
                  <Badge variant="warning">No verificado</Badge>
                )}
              </div>
              <div className="flex items-center gap-4 text-sm">
                <span className="text-green-600" title="Ventas">V: {formatMonto(sorteo.total_ventas_sorteo)}</span>
                <span className="text-red-600" title="Premios">P: {formatMonto(sorteo.total_sorteo)}</span>
                <span className={`font-bold ${esSorteoPositivo ? 'text-green-600' : 'text-red-600'}`} title="Balance">
                  {formatMonto(sorteo.balance_sorteo)}
                </span>
              </div>
            </button>

            {/* Detalle por local */}
            {isExpanded && (
              <div className="mt-4 space-y-3 border-t pt-4">
                {sorteo.locales.length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-2">Sin jugadas ganadoras en este sorteo</p>
                )}
                {sorteo.locales.map(local => {
                  const localKey = `${sorteo.sorteo_id}:${local.local_id}`;
                  const isLocalExpanded = expandedLocales.has(localKey);

                  return (
                    <div key={local.local_id} className="border rounded-lg">
                      {/* Header local */}
                      <button
                        onClick={() => toggleLocal(localKey)}
                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50"
                      >
                        <div className="flex items-center gap-2">
                          {isLocalExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          <Store size={16} className="text-gray-400" />
                          <span className="font-medium text-gray-800">{local.local_nombre}</span>
                          <span className="text-xs text-gray-400">({local.jugadas_ganadoras.length} jugadas)</span>
                        </div>
                        <span className="font-bold text-gray-900">{formatMonto(local.total_local)}</span>
                      </button>

                      {/* Warnings */}
                      {local.warnings.length > 0 && (
                        <div className="mx-4 mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-yellow-700 text-xs">
                          {local.warnings.join(' | ')}
                        </div>
                      )}

                      {/* Tabla de jugadas ganadoras */}
                      {isLocalExpanded && (
                        <div className="px-4 pb-3">
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-left text-gray-500 border-b">
                                  <th className="py-2 pr-2">Ticket</th>
                                  <th className="py-2 pr-2">Numero</th>
                                  <th className="py-2 pr-2">Concepto</th>
                                  <th className="py-2 pr-2 text-right">Jugado</th>
                                  <th className="py-2 pr-2 text-right">Multi.</th>
                                  <th className="py-2 pr-2 text-right">Pago</th>
                                  <th className="py-2">Regla</th>
                                </tr>
                              </thead>
                              <tbody>
                                {local.jugadas_ganadoras.map((jg, idx) => {
                                  const badge = REGLA_BADGE[jg.regla_origen] || REGLA_BADGE.SIN_REGLA;
                                  return (
                                    <tr
                                      key={idx}
                                      className={`border-b last:border-0 ${jg.regla_origen === 'SIN_REGLA' ? 'bg-red-50' : ''}`}
                                    >
                                      <td className="py-2 pr-2 font-mono text-xs">{jg.ticket_number ? formatTicketForPrint(jg.ticket_number) : jg.ticket_id.slice(0, 10)}</td>
                                      <td className="py-2 pr-2 font-mono">
                                        #{jg.num1}{jg.num2 ? ` / ${jg.num2}` : ''}
                                      </td>
                                      <td className="py-2 pr-2">{CONCEPTO_LABELS[jg.concepto] || jg.concepto}</td>
                                      <td className="py-2 pr-2 text-right">{formatMonto(jg.monto_jugado)}</td>
                                      <td className="py-2 pr-2 text-right font-medium">x{jg.multiplicador}</td>
                                      <td className="py-2 pr-2 text-right font-bold">{formatMonto(jg.pago)}</td>
                                      <td className="py-2">
                                        <Badge variant={badge.variant}>{badge.label}</Badge>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
};


// ========================
// COMPONENTE PRINCIPAL CON TABS
// ========================
export const PremiosPage: React.FC = () => {
  return (
    <Tabs
      items={[
        {
          key: 'premios',
          label: 'Premios',
          icon: <Trophy size={16} />,
          content: <PremiosTab />,
        },
        {
          key: 'premios-local',
          label: 'Premios por Local',
          icon: <Store size={16} />,
          content: <PremiosPorLocalTab />,
        },
        // Pestaña "Reglas de Premios" oculta - usar Paquetes de Reglas en su lugar
      ]}
    />
  );
};

export default PremiosPage;
